import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Detect hydration mismatch errors that were likely caused by in-page browser
 * translation (Google Translate, Apple Translate, Edge Translate, Yandex).
 *
 * These tools mutate text nodes in the server-rendered DOM before React hydrates,
 * producing a mismatch that React reports as a hydration error. Since the site
 * provides its own language picker, these are considered non-actionable noise.
 *
 * Returns true only when BOTH conditions are met:
 * 1. The error is specifically a hydration content mismatch
 * 2. A known translation engine has injected markers into the DOM
 */
export function isTranslationHydrationError(event: ErrorEvent): boolean {
	const isHydrationMismatch = event.exception?.values?.some(
		(e) =>
			e.value?.includes("server rendered HTML didn't match") ||
			e.value?.includes('Text content does not match') ||
			e.value?.includes('Minified React error #418'),
	);
	if (!isHydrationMismatch) return false;

	return hasBrowserTranslationMarkers();
}

export function filterTranslationHydrationError(event: ErrorEvent): ErrorEvent | null {
	return isTranslationHydrationError(event) ? null : event;
}

/**
 * Check for DOM markers injected by known browser translation engines.
 * Extracted for testability — in unit tests this can be validated against
 * a mocked document.
 */
export function hasBrowserTranslationMarkers(): boolean {
	const html = document.documentElement;
	return (
		html.classList.toString().includes('translated') ||
		html.hasAttribute('_msttexthash') ||
		document.getElementById('goog-gt-tt') !== null ||
		document.getElementById('ytWidget') !== null
	);
}
